import { computed, effect, inject, Injectable } from '@angular/core';
import { OrderApiResponseStateService } from './order-api-response-state.service';
import { BasePdResponseService } from './base-pd-response.service';
import { AdditionalOfferData, OfferType } from '../../../../../models/order/workflow/additional-offer.model';
import { FlattenCustomization } from '../../../../../models/order/api-models/order-pd-response.model';
import { OrderFlowType } from '../../../../../models/order/order.model';

@Injectable({ providedIn: 'root' })
export class InternetProductResponseService extends BasePdResponseService {
    private orderApiResponseStateService = inject(OrderApiResponseStateService);
    private readonly internetAbpCustomizationCode = 'ATT-INTERNET-ABP-PAPERLESS-REQ';
    private readonly internetPaperlessCustomizationCode = 'INTERNET-INCLUDED-OFFERS-PAPERLESS';
    override productResponse = computed(() => this.orderApiResponseStateService.internetProductDetailsData());

    updateOfferResponse = computed(() => {
        return this.orderApiResponseStateService.internetUpdateOfferData();
    });

    getProductComponent() {
        const pdResponse = this.productResponse();
        return pdResponse?.Components?.Component.find(c => c.ProductType?.toUpperCase() === 'INTERNET' || c.ProductType?.toUpperCase() === 'OOF');
    }

    constructor() {
        super();
        effect(() => {
            const pdResponse = this.productResponse();
            if (pdResponse) {
                this.setupCustomizations();
            } else {
                this.customizations.set([]);
            }
        });
    }

    override setupCustomizations() {
        const tempCustomizations = [];

        // 1. PD customizations first
        const pdCustomizations = this.getProductComponent();
        if (pdCustomizations) {
            const customizations = this.flattenCustomizations(pdCustomizations, OrderFlowType.Internet);
            customizations.sort((a, b) => a.customization.Seq - b.customization.Seq);
            tempCustomizations.push(...customizations);
        }

        // 2. UO customizations
        const updateOfferComponent = this.getUpdateOfferProductComponent();
        if (updateOfferComponent) {
            const uoCustomizations = this.flattenCustomizations(updateOfferComponent, OrderFlowType.Internet);

            uoCustomizations.forEach(uoCust => {
                const existingIndex = tempCustomizations.findIndex(
                    ecust => ecust.customization.CustomizationCode === uoCust.customization.CustomizationCode
                );
                if (existingIndex > -1) {
                    tempCustomizations[existingIndex] = uoCust;
                } else {
                    tempCustomizations.push(uoCust);
                }
            });
        }
        this.customizations.set(tempCustomizations);
    }

    getABPAdditionalOfferData(abpEnabled: boolean): AdditionalOfferData {
        const abpChoiceCode = abpEnabled ? 'ATT-INTERNET-ABP-BOTH-YES' : 'ATT-INTERNET-ABP-PAPERLESS-YES';
        return {
            choiceCode: abpChoiceCode,
            customizationCode: this.internetAbpCustomizationCode,
            type: OfferType.Required,
            usoc: abpChoiceCode
        }
    }

    isInternetAbpCustomization(customization: FlattenCustomization): boolean {
        const customizationCode = customization?.customization?.CustomizationCode;
        const subGroupName = customization?.customization?.SubGroupName;
        return customizationCode === this.internetAbpCustomizationCode || subGroupName === 'ABP';
    }

    isInternetPaperlessCustomization(customization: FlattenCustomization): boolean {
        const customizationCode = customization?.customization?.CustomizationCode;
        const subGroupName = customization?.customization?.SubGroupName;
        return customizationCode === this.internetPaperlessCustomizationCode || subGroupName === 'PaperlessSelection';
    }

    getFlowVisibleCustomizationsIncludingAbp(): FlattenCustomization[] {
        const flowVisible = this.getFlowVisibleCustomizations()?.filter(cust => cust.customization.Category?.toLocaleLowerCase() !== 'INTERNET');
        const allCustomizations = this.getCustomizations();
        const requiredAbp = allCustomizations.filter(customization => this.isInternetAbpCustomization(customization));
        const requiredPaperless = allCustomizations.filter(customization => this.isInternetPaperlessCustomization(customization));
        const voiceOffers = allCustomizations.filter(cust => cust.customization.SubGroupName === 'VoiceNewListing' || cust.customization.SubGroupName === 'VoiceNewLD' || cust.customization.SubGroupName === 'VoiceNewFreeze' || cust.customization.SubGroupName === 'VoicePortListing' || cust.customization.SubGroupName === 'VoicePortLD' || cust.customization.SubGroupName === 'VoicePortFreeze');
        const mergedByCustomizationCode = new Map<string, FlattenCustomization>();
        [...flowVisible, ...voiceOffers, ...requiredAbp, ...requiredPaperless].forEach(customization => {
            const customizationCode = customization.customization.CustomizationCode;
            if (!mergedByCustomizationCode.has(customizationCode)) {
                mergedByCustomizationCode.set(customizationCode, customization);
            }
        });

        return Array.from(mergedByCustomizationCode.values());
    }

    getUpdateOfferProductComponent() {
        const updateOfferResponse = this.updateOfferResponse();
        return updateOfferResponse?.Components?.Component;
    }

}